import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { LocationOn as LocationIcon } from '@mui/icons-material';
import { repAPI } from '../../services/api';

const RepOwnInventory = () => {
  const [inventory, setInventory] = useState([]);
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const response = await repAPI.getMyOwnInventory();
      if (response.success) {
        setInventory(response.data || []);
        setLocation(response.location || 'Not set');
      }
    } catch (err) {
      setError('Failed to load your inventory');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      AVAILABLE: 'success',
      RESERVED: 'warning',
      ALLOCATED: 'info',
      SOLD: 'default',
      EXPIRED: 'error'
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Card variant="outlined" sx={{ bgcolor: '#f0fdf4', borderColor: '#22c55e' }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <LocationIcon sx={{ color: '#22c55e', fontSize: 32 }} />
              </Grid>
              <Grid item xs>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Your Inventory Location
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#22c55e' }}>
                  {location}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {!location || location === 'Not set' ? (
        <Alert severity="info">
          No inventory location assigned. Contact admin to set up your inventory location.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Batch ID</TableCell>
                <TableCell>Lot Number</TableCell>
                <TableCell>Quantity Available</TableCell>
                <TableCell>Expiration Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Location</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No inventory assigned to your location yet
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                inventory.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {item.product?.name || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {item.batchId || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {item.lotNumber || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.quantityAvailable || 0} 
                        color="success" 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      {item.expirationDate 
                        ? new Date(item.expirationDate).toLocaleDateString()
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.qcStatus || 'PENDING'} 
                        color={getStatusColor(item.qcStatus)} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {item.location || location}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default RepOwnInventory;

